const LEGACY_STORAGE_KEY = "supabase.auth.token";

const getProjectRef = (): string | null => {
  const url = process.env.REACT_APP_SUPABASE_URL;
  if (!url) return null;
  try {
    const hostname = new URL(url).hostname;
    return hostname.split(".")[0] || null;
  } catch (error) {
    console.warn("[supabase] Failed to parse project ref", error);
    return null;
  }
};

export const getSupabaseStorageKeys = (): {
  legacyKey: string;
  defaultKey: string | null;
} => {
  const projectRef = getProjectRef();
  const defaultKey = projectRef ? `sb-${projectRef}-auth-token` : null;
  return { legacyKey: LEGACY_STORAGE_KEY, defaultKey };
};

const readStorageValue = (key: string | null): string | null => {
  if (!key || typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
};

export const migrateLegacySupabaseSession = (): void => {
  if (typeof window === "undefined") return;
  const { legacyKey, defaultKey } = getSupabaseStorageKeys();
  if (!defaultKey) return;
  const legacyValue = readStorageValue(legacyKey);
  const defaultValue = readStorageValue(defaultKey);
  if (legacyValue && !defaultValue) {
    window.localStorage.setItem(defaultKey, legacyValue);
    console.debug("[supabase] Migrated legacy auth storage key");
  }
};

export const getCachedSupabaseSession = (): any | null => {
  if (typeof window === "undefined") return null;
  const { legacyKey, defaultKey } = getSupabaseStorageKeys();
  // Prefer the current Supabase storage key over legacy.
  const keys = [defaultKey, legacyKey].filter(Boolean) as string[];
  for (const key of keys) {
    const raw = readStorageValue(key);
    if (!raw) continue;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("[supabase] Failed to parse cached session", error);
    }
  }
  return null;
};

export const getCachedAccessToken = (): string | null => {
  const cached = getCachedSupabaseSession();
  if (!cached) return null;
  return cached.access_token || cached.currentSession?.access_token || null;
};

export const getCachedUser = (): {
  id?: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
} | null => {
  const cached = getCachedSupabaseSession();
  if (!cached) return null;
  return cached.user || cached.currentSession?.user || null;
};

export const clearCachedSupabaseSession = (): void => {
  if (typeof window === "undefined") return;
  const { legacyKey, defaultKey } = getSupabaseStorageKeys();
  window.localStorage.removeItem(legacyKey);
  if (defaultKey) {
    window.localStorage.removeItem(defaultKey);
  }
};
